/**
 * Centralized Z-Index Management System
 *
 * This file defines all z-index values used throughout the application
 * to prevent conflicts and ensure predictable stacking order.
 *
 * Layer ranges are designed with gaps to allow for future additions
 * without breaking the hierarchy.
 */

export const Z_INDEX_LAYERS = {
  // Base layer - document content, basic UI elements
  BASE: {
    CONTENT: 1,
    TABLE_HEADERS: 2,
    SVG_OVERLAY: 5,
  },

  // UI Components layer - 100-199
  UI_COMPONENTS: {
    TOOLBAR: 100,
    TOOLBAR_CONTAINER: 110,
    SIDEBAR: 120,
    TAKEOFF_PALETTE: 130,
    FILTER_BAR: 140,
  },

  // Dropdowns and menus layer - 200-299
  DROPDOWNS: {
    SELECT_MENUS: 200,
    CONTEXT_MENUS: 210,
    AUTOCOMPLETE: 220,
  },

  // Overlays and panels layer - 300-399
  OVERLAYS: {
    PANELS: 300,
    SLIDE_OUTS: 310,
    COLLAPSIBLE: 320,
  },

  // Floating elements layer - 400-499
  FLOATING: {
    TOOLTIPS: 400,
    POPOVERS: 410,
    FLOATING_BUTTONS: 420,
  },

  // Modals and dialogs layer - 500-599
  MODALS: {
    BACKGROUND: 500,
    DIALOG: 510,
    ALERT: 520,
    CONFIRMATION: 530,
  },

  // Editing interfaces layer - 600-699
  EDITORS: {
    POINT_EDITOR: 600,
    COLOR_EDITOR: 610,
    TEXT_EDITOR: 620,
    INLINE_EDITOR: 630,
  },

  // Annotations layer - 700-799
  ANNOTATIONS: {
    NOTES: 700,
    HIGHLIGHTS: 710,
    DRAWINGS: 720,
  },

  // System overlays layer - 800-899
  SYSTEM: {
    TAKEOFF_PALETTE: 800,
    HELP_INSTRUCTIONS: 810,
    DOCUMENT_MANAGER: 850,
    FILE_UPLOAD: 860,
  },

  // Critical system layer - 900-999
  CRITICAL: {
    CHAT_WINDOW: 900,
    ERROR_OVERLAY: 910,
    LOADING_OVERLAY: 920,
    GLOBAL_SPINNER: 930,
  },

  // Maximum priority layer - 1000+
  MAXIMUM: {
    FULLSCREEN_MODAL: 1000,
    EMERGENCY_OVERLAY: 1100,
    DEBUG_TOOLS: 9999,
  },
} as const;

/**
 * Helper function to get z-index value with type safety
 */
export const getZIndex = (layer: keyof typeof Z_INDEX_LAYERS, item: string): number => {
  const layerObj = Z_INDEX_LAYERS[layer] as Record<string, number>;
  const value = layerObj[item];

  if (value === undefined) {
    console.warn(`Z-index not found for ${layer}.${item}. Using default value 1.`);
    return 1;
  }

  return value;
};

/**
 * Helper to create stacking context isolation
 */
export const createStackingContext = {
  isolate: {
    isolation: 'isolate' as const,
  },

  contain: {
    contain: 'layout style' as const,
  },

  full: {
    isolation: 'isolate' as const,
    contain: 'layout style' as const,
  },
};

/**
 * Common positioning patterns
 */
export const positionPatterns = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
  },

  absoluteTopRight: {
    position: 'absolute' as const,
    top: 0,
    right: 0,
  },

  absoluteCenter: {
    position: 'absolute' as const,
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },

  stickyTop: {
    position: 'sticky' as const,
    top: 0,
  },
};
